import React, { useState } from 'react'
import { Checkbox, ConfirmationDialog, FormControlLabel, makeStyles, Typography } from '@paintscout/ui'
import type { ConfirmationDialogProps, MuiTheme as Theme } from '@paintscout/ui'

export interface ArchiveDealDialogProps extends Omit<ConfirmationDialogProps, 'onConfirm' | 'message' | 'title'> {
  /**
   * Whether the deal has any linked estimates/invoices. When true the dialog offers the
   * "also archive the linked estimate/invoice" checkbox (ENG-773).
   */
  showArchiveEstimates?: boolean
  /** Checkbox label, built by the caller from the object labels of what's actually linked. */
  archiveEstimatesLabel?: string
  /** How many deals are archived. More than 1 switches the copy to plural (bulk archive from the deals list). */
  dealCount?: number
  /** Confirm handler — receives whether the user opted to also archive the linked estimate(s)/invoice(s). */
  onConfirm: (opts: { archiveEstimates: boolean }) => void | Promise<void>
  onCancel: () => void
}

const useStyles = makeStyles<Theme>(
  (theme) => ({
    message: {
      marginBottom: theme.spacing(2)
    }
  }),
  { name: 'ArchiveDealDialog' }
)

/**
 * Confirmation dialog shown before archiving a deal (ENG-773). Matches the "Delete" flow's
 * are-you-sure pattern but adds an opt-in checkbox to archive the deal's linked estimate(s) and
 * invoice(s) at the same time.
 */
function ArchiveDealDialog({
  showArchiveEstimates = false,
  archiveEstimatesLabel = 'Also archive the linked estimate and invoice',
  dealCount = 1,
  onConfirm,
  ...props
}: ArchiveDealDialogProps) {
  const classes = useStyles()
  const [archiveEstimates, setArchiveEstimates] = useState(false)
  const plural = dealCount > 1

  return (
    <ConfirmationDialog
      {...props}
      title={plural ? `Archive these ${dealCount} deals?` : 'Archive this deal?'}
      yesLabel="Yes, I'm Sure"
      hideBackButton
      message={
        <>
          <Typography variant="body1" component="div" className={showArchiveEstimates ? classes.message : undefined}>
            {plural
              ? 'You can find them anytime by filtering for archived deals and bring them back whenever you need.'
              : 'You can find it anytime by filtering for archived deals and bring it back whenever you need.'}
          </Typography>
          {showArchiveEstimates && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={archiveEstimates}
                  onChange={(_ev: React.ChangeEvent<HTMLInputElement>, value: boolean) => setArchiveEstimates(value)}
                />
              }
              label={archiveEstimatesLabel}
            />
          )}
        </>
      }
      onConfirm={() => onConfirm({ archiveEstimates })}
    />
  )
}

export default ArchiveDealDialog
